<template>
  <v-card flat class="dq-panel">
    <v-card-text class="d-flex align-center flex-wrap pa-4">
      <div class="dq-icon mr-3" :class="{ 'dq-icon--ok': isClean }">
        <v-icon size="24" color="white">
          {{ isClean ? "mdi-shield-check" : "mdi-shield-alert" }}
        </v-icon>
      </div>
      <div class="mr-auto">
        <div class="dq-title">ตรวจสอบข้อมูลผิดปกติของสถานศึกษา</div>
        <div class="dq-subtitle">
          <template v-if="generatedAt">ตรวจล่าสุด {{ generatedAtText }} · </template>
          ระบบตรวจข้อมูลบุคลากร พนักงานราชการ และรายการย้ายของสถานศึกษาทุกครั้งที่เปิดหน้านี้
        </div>
      </div>
      <v-btn
        outlined
        small
        color="primary"
        :loading="loading"
        class="mt-2 mt-sm-0"
        @click="load()"
      >
        <v-icon small left>mdi-refresh</v-icon>ตรวจสอบใหม่
      </v-btn>
    </v-card-text>

    <v-divider />

    <div v-if="loading && !checks.length" class="pa-6 text-center grey--text text--darken-1">
      <v-progress-circular indeterminate size="22" width="2" color="primary" class="mr-2" />
      กำลังตรวจสอบข้อมูล...
    </div>

    <v-alert v-else-if="loadError" type="error" text dense class="ma-4">
      {{ loadError }}
      <v-btn small text color="error" @click="load()">ลองใหม่</v-btn>
    </v-alert>

    <div v-else-if="isClean" class="pa-5 d-flex align-center">
      <v-icon color="success" class="mr-3">mdi-check-circle</v-icon>
      <div>
        <div class="font-weight-bold">ไม่พบข้อมูลผิดปกติ</div>
        <div class="text-caption grey--text text--darken-1">
          ข้อมูลของสถานศึกษาผ่านการตรวจสอบทุกรายการ
          <span v-if="totals.acked"> (มี {{ totals.acked }} รายการที่ยืนยันแล้วว่าถูกต้อง)</span>
        </div>
      </div>
    </div>

    <div v-else>
      <div class="px-4 pt-3 d-flex align-center flex-wrap">
        <v-chip small dark color="#B3151B" class="mr-2 mb-1">
          <v-icon x-small left>mdi-alert-circle</v-icon>ต้องแก้ไข {{ totals.error }}
        </v-chip>
        <v-chip small dark color="#A86400" class="mr-2 mb-1">
          <v-icon x-small left>mdi-alert</v-icon>ควรตรวจสอบ {{ totals.warning }}
        </v-chip>
        <v-chip v-if="totals.acked" small outlined class="mb-1">
          <v-icon x-small left>mdi-check</v-icon>ยืนยันแล้ว {{ totals.acked }}
        </v-chip>
      </div>

      <v-list two-line class="py-0">
        <template v-for="(c, i) in activeChecks">
          <v-divider v-if="i > 0" :key="'d' + c.code" />
          <v-list-item :key="c.code" class="dq-item">
            <v-list-item-avatar size="34" :color="c.severity === 'error' ? '#FBECEC' : '#FFF4DC'">
              <v-icon small :color="c.severity === 'error' ? '#B3151B' : '#A86400'">
                {{ c.severity === "error" ? "mdi-alert-circle" : "mdi-alert" }}
              </v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="dq-item-title">
                {{ c.title }}
                <v-chip x-small label class="ml-2 dq-count" :color="c.severity === 'error' ? '#B3151B' : '#A86400'" dark>
                  {{ c.count }}{{ c.capped ? "+" : "" }}
                </v-chip>
                <v-chip v-if="c.owner === 'central'" x-small label outlined class="ml-1">ส่วนกลางแก้ไข</v-chip>
              </v-list-item-title>
              <v-list-item-subtitle class="dq-item-desc">{{ c.description }}</v-list-item-subtitle>
              <v-list-item-subtitle v-if="c.samples.length" class="dq-item-sample">
                เช่น {{ c.samples.map(s => s.name || s.info).join(" · ") }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="flex-row align-center">
              <v-btn small depressed color="primary" @click="openDetail(c)">ดูรายการ</v-btn>
            </v-list-item-action>
          </v-list-item>
        </template>
      </v-list>
    </div>

    <v-dialog v-model="dialog" max-width="900" scrollable>
      <v-card v-if="current">
        <div class="dq-dialog-title d-flex align-center px-6 pt-5">
          <v-icon :color="current.severity === 'error' ? '#B3151B' : '#A86400'" class="mr-2">
            {{ current.severity === "error" ? "mdi-alert-circle" : "mdi-alert" }}
          </v-icon>
          {{ current.title }}
        </div>
        <v-card-text class="pt-3">
          <div class="text-body-2 mb-1">{{ current.description }}</div>
          <v-alert dense text color="#455A64" icon="mdi-lightbulb-on-outline" class="mb-3 text-body-2">
            <strong>แนวทางแก้ไข:</strong> {{ current.fix }}
          </v-alert>
          <div class="d-flex align-center mb-2">
            <v-switch
              v-model="showAcked"
              dense
              hide-details
              class="mt-0 pt-0"
              label="แสดงรายการที่ยืนยันแล้วว่าถูกต้อง"
            />
            <v-spacer />
            <span class="text-caption grey--text text--darken-1">
              เหลือรายการที่ต้องตรวจ {{ detailOpenCount }} รายการ
              <template v-if="detailCapped"> (แสดงสูงสุด 500)</template>
            </span>
          </div>
          <v-data-table
            :headers="headers"
            :items="visibleItems"
            :loading="detailLoading"
            :items-per-page="10"
            dense
            no-data-text="ไม่มีรายการที่ต้องตรวจสอบ"
          >
            <template v-slot:item.name="{ item }">
              <span :class="{ 'grey--text': item.acked }">{{ item.name || "-" }}</span>
            </template>
            <template v-slot:item.info="{ item }">
              <span class="text-caption" :class="{ 'grey--text': item.acked }">{{ item.info }}</span>
              <div v-if="item.acked" class="text-caption success--text">
                <v-icon x-small color="success">mdi-check</v-icon>
                ยืนยันแล้ว{{ item.ack_note ? " — " + item.ack_note : "" }}
              </div>
            </template>
            <template v-slot:item.action="{ item }">
              <v-btn
                v-if="ackAvailable && !item.acked"
                x-small
                outlined
                color="success"
                @click="ack(item)"
              >
                ตรวจสอบแล้ว ถูกต้อง
              </v-btn>
              <v-btn v-else-if="ackAvailable && item.acked" x-small text @click="unack(item)">
                ยกเลิกการยืนยัน
              </v-btn>
            </template>
          </v-data-table>
          <div v-if="!ackAvailable" class="text-caption grey--text mt-2">
            * ปุ่ม "ตรวจสอบแล้ว ถูกต้อง" ยังไม่เปิดใช้งาน (รอผู้ดูแลระบบติดตั้งตารางบันทึกการตรวจสอบ)
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="dialog = false">ปิด</v-btn>
          <v-spacer />
          <v-btn depressed color="primary" @click="goFix(current)">
            <v-icon small left>mdi-open-in-new</v-icon>
            {{ current.owner === "central" ? "เปิดหน้าข้อมูลที่เกี่ยวข้อง" : "ไปหน้าแก้ไขข้อมูล" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
// แผงตรวจสอบข้อมูลผิดปกติของสถานศึกษา — เรียก college_data_quality.php (ตรวจตาม session ของสถานศึกษาเอง)
// โหลดใหม่ทุกครั้งที่เข้าหน้า, เมื่อกลับมาที่แท็บนี้ (focus) หลังผ่านไปเกิน 2 นาที และเมื่อกด "ตรวจสอบใหม่"
export default {
  name: "DataQualityPanel",
  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      loadError: "",
      generatedAt: "",
      totals: { error: 0, warning: 0, acked: 0 },
      checks: [],
      ackAvailable: false,
      dialog: false,
      current: null,
      items: [],
      detailLoading: false,
      detailCapped: false,
      showAcked: false,
      lastLoadedMs: 0,
      headers: [
        { text: "ชื่อ-สกุล / รายการ", value: "name" },
        { text: "รายละเอียด", value: "info" },
        { text: "", value: "action", sortable: false, align: "end" }
      ]
    };
  },
  computed: {
    activeChecks() {
      return this.checks.filter(c => c.count > 0);
    },
    isClean() {
      return !this.loading && !this.loadError && this.checks.length > 0 && this.activeChecks.length === 0;
    },
    generatedAtText() {
      const d = new Date(this.generatedAt.replace(" ", "T"));
      if (isNaN(d.getTime())) return this.generatedAt;
      return d.toLocaleString("th-TH", { dateStyle: "medium", timeStyle: "short" });
    },
    visibleItems() {
      return this.showAcked ? this.items : this.items.filter(i => !i.acked);
    },
    detailOpenCount() {
      return this.items.filter(i => !i.acked).length;
    }
  },
  mounted() {
    this.load();
    window.addEventListener("focus", this.onFocus);
  },
  beforeDestroy() {
    window.removeEventListener("focus", this.onFocus);
  },
  methods: {
    onFocus() {
      if (Date.now() - this.lastLoadedMs > 120000 && !this.dialog) this.load();
    },
    async call(payload) {
      try {
        const res = await this.$http.post("college_data_quality.php", { ApiKey: this.ApiKey, ...payload });
        return res.data;
      } catch (e) {
        const d = e.response && e.response.data;
        return { status: false, message: (d && d.message) || "เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ" };
      }
    },
    async load() {
      this.loading = true;
      this.loadError = "";
      const data = await this.call({ action: "summary" });
      this.loading = false;
      if (data.status) {
        this.checks = data.checks;
        this.totals = data.totals;
        this.generatedAt = data.generated_at;
        this.ackAvailable = data.ack_available;
        this.lastLoadedMs = Date.now();
      } else {
        this.loadError = data.message || "ตรวจสอบข้อมูลไม่สำเร็จ";
      }
    },
    async openDetail(check) {
      this.current = check;
      this.items = [];
      this.showAcked = false;
      this.dialog = true;
      await this.loadDetail();
    },
    async loadDetail() {
      this.detailLoading = true;
      const data = await this.call({ action: "detail", code: this.current.code });
      this.detailLoading = false;
      if (data.status) {
        this.items = data.items;
        this.detailCapped = data.capped;
        this.ackAvailable = data.ack_available;
      } else {
        this.dialog = false;
        Swal.fire({ icon: "warning", title: data.message || "โหลดรายการไม่สำเร็จ" });
      }
    },
    async ack(item) {
      const r = await Swal.fire({
        title: "ยืนยันว่าข้อมูลรายการนี้ถูกต้อง?",
        text: item.name ? item.name : item.info,
        input: "text",
        inputPlaceholder: "หมายเหตุ (ไม่บังคับ) เช่น เป็นคนละคน ชื่อซ้ำกัน",
        inputAttributes: { maxlength: 200 },
        showCancelButton: true,
        confirmButtonText: "ยืนยัน ข้อมูลถูกต้อง",
        cancelButtonText: "ยกเลิก"
      });
      if (!r.isConfirmed) return;
      await this.changeAck("ack", item, r.value || "");
    },
    async unack(item) {
      await this.changeAck("unack", item, "");
    },
    async changeAck(action, item, note) {
      const data = await this.call({ action, code: this.current.code, item_key: item.item_key, note });
      if (!data.status) {
        Swal.fire({ icon: "warning", title: data.message || "บันทึกไม่สำเร็จ" });
        await this.loadDetail(); // รายการอาจถูกแก้ไขไปแล้ว
        return;
      }
      await Promise.all([this.loadDetail(), this.load()]);
    },
    goFix(check) {
      this.dialog = false;
      this.$router.push(check.route).catch(() => {});
    }
  }
};
</script>

<style scoped>
.dq-panel {
  border-radius: 14px !important;
  border: 1px solid #e4e4e7 !important;
  background: #ffffff !important;
}
.dq-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #b3151b;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.dq-icon--ok {
  background: #2e7d32;
}
.dq-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.3;
}
.dq-subtitle {
  font-size: 0.8rem;
  color: #616161;
  margin-top: 2px;
}
.dq-item {
  padding-top: 8px;
  padding-bottom: 8px;
}
.dq-item-title {
  font-weight: 600;
  white-space: normal;
}
.dq-item-desc,
.dq-item-sample {
  white-space: normal !important;
  font-size: 0.8rem !important;
  color: #616161 !important;
}
.dq-count {
  font-weight: 700;
}
.dq-dialog-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a1a1a;
}
</style>
